import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/api"; // Assuming your configured axios instance
import {
  FaCalendar,
  FaDollarSign,
  FaTruck,
  FaCreditCard,
  FaExclamationCircle,
  FaBox,
  FaArrowLeft,
} from "react-icons/fa";
import {
  formatCurrency,
  formatDateTime,
  getStatusBadge,
} from "../../utils/helpers";
import { getImageUrl } from "../../utils/imageUrl";
import { useTranslation } from "react-i18next";

const OrderDetailsPage = () => {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        // NOTE: Your backend MUST be configured to EAGER-LOAD (include)
        // OrderItems, ShippingAddress, and PaymentMethod.
        const response = await api.get(`/orders/${orderId}`);
        setOrder(response.data);
      } catch (err) {
        console.error("Failed to fetch order details:", err);
        setError(t("profile.orderDetails.loadError"));
      } finally {
        setLoading(false);
      }
    };
    fetchOrderDetails();
  }, [orderId, t]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl text-gray-600">
          {t("profile.orderDetails.loading", { orderId })}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-700 bg-red-100 rounded-lg max-w-4xl mx-auto mt-10">
        <FaExclamationCircle className="inline-block mr-2" /> {error}
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6 text-center text-gray-500 mt-10">
        {t("profile.orderDetails.noData")}
      </div>
    );
  }

  // De-structure complex objects for easier use
  const shippingAddress = order.ShippingAddress; // Assuming this relationship is loaded
  const paymentMethod = order.PaymentMethod; // Assuming this relationship is loaded
  const orderItems = order.OrderItems || []; // Assuming this relationship is loaded

  // Calculate item total cost
  const itemsTotal = orderItems.reduce(
    (sum, item) => sum + item.quantity * item.price,
    0,
  );

  // Determine card icon based on brand (a basic example)
  const getCardIcon = (brand) => {
    if (!brand) return <FaCreditCard />;
    if (brand.toLowerCase().includes("visa"))
      return <i className="fab fa-cc-visa"></i>; // Requires Font Awesome Pro or CDN
    // Fallback to generic icon if no specific brand icon is used
    return <FaCreditCard />;
  };

  return (
    <div className="canvas-background w-full max-w-full container mx-auto p-6 lg:px-[11rem] lg:py-[9rem] min-h-screen">
      <div className="bg-white rounded-lg shadow-2xl p-8">
        {/* --- BACK BUTTON --- */}
        <button
          onClick={() => navigate("/profile?tab=orders")} // 👈 Navigation logic
          className="flex items-center text-lg text-gray-600 hover:text-[#83375B] transition duration-150 mb-6"
        >
          <FaArrowLeft className="mr-2" />
          Back to Orders
        </button>
        {/* --- HEADER: ORDER SUMMARY & STATUS --- */}
        <header className="border-b pb-6 mb-6">
          <h1 className="text-4xl font-extrabold text-[#83375B] mb-2">
            Order #{order.order_number || order.order_id}
          </h1>

          <div className="flex flex-wrap items-center space-x-4 text-gray-600 text-lg">
            <span className="flex items-center">
              <FaCalendar className="mr-2 text-gray-500" />
              Placed on:{" "}
              <span className="font-semibold ml-1">
                {formatDateTime(order.createdAt)}
              </span>
            </span>
            <span className="flex items-center">
              <FaDollarSign className="mr-2 text-green-600" />
              Total:{" "}
              <span className="font-extrabold text-2xl text-green-700 ml-1">
                {formatCurrency(order.total_price)}
              </span>
            </span>
            <span
              className={`px-3 py-1 text-base font-bold rounded-full capitalize ${getStatusBadge(
                order.status,
              )}`}
            >
              {order.status}
            </span>
          </div>
        </header>

        {/* --- SHIPPING AND PAYMENT DETAILS --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* SHIPPING ADDRESS PANEL */}
          <div className="border p-5 rounded-lg bg-gray-50 shadow-sm">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaTruck className="mr-2 text-[#83375B]" /> Shipping Information
            </h2>
            {shippingAddress ? (
              <div className="space-y-1 text-gray-700">
                <p className="font-semibold">
                  {shippingAddress.full_name ||
                    order.user_first_name + " " + order.user_last_name}
                </p>
                <p>{shippingAddress.address_line_1}</p>
                {shippingAddress.address_line_2 && (
                  <p>{shippingAddress.address_line_2}</p>
                )}
                <p>
                  {shippingAddress.city}, {shippingAddress.state_province}{" "}
                  {shippingAddress.postal_code}
                </p>
                <p className="font-medium mt-2">
                  {order.shipping_method || "Standard Shipping"}
                </p>
                {order.tracking_number && (
                  <p className="text-sm pt-2 border-t mt-2">
                    **Tracking No:**{" "}
                    <a
                      href={`#tracking-link-placeholder/${order.tracking_number}`}
                      className="text-blue-600 hover:underline"
                    >
                      {order.tracking_number}
                    </a>
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 italic">
                Shipping address details unavailable.
              </p>
            )}
          </div>

          {/* PAYMENT METHOD PANEL */}
          <div className="border p-5 rounded-lg bg-gray-50 shadow-sm">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaCreditCard className="mr-2 text-blue-500" /> Payment Method
            </h2>
            {paymentMethod ? (
              <div className="space-y-1 text-gray-700">
                <p className="text-lg font-bold flex items-center">
                  {getCardIcon(paymentMethod.card_brand)}
                  <span className="ml-2">
                    {paymentMethod.card_brand || paymentMethod.method_type}
                  </span>
                </p>
                {paymentMethod.last4 && (
                  <p className="font-mono text-xl tracking-wider">
                    **** **** **** {paymentMethod.last4}
                  </p>
                )}
                {paymentMethod.cardholder_name && (
                  <p className="text-sm">
                    Cardholder: {paymentMethod.cardholder_name}
                  </p>
                )}
                <p className="text-sm">
                  Payment Status:{" "}
                  <span className="font-semibold capitalize text-green-600">
                    {order.payment_status}
                  </span>
                </p>
                <p className="text-xs text-gray-500 pt-2 border-t mt-2">
                  Transaction ID: {order.transaction_id || "N/A"}
                </p>
              </div>
            ) : (
              <p className="text-gray-500 italic">
                Payment method details unavailable.
              </p>
            )}
          </div>
        </div>

        {/* --- ORDER ITEMS TABLE --- */}
        <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2 flex items-center">
          <FaBox className="mr-2 text-[#83375B]" /> Items in This Order (
          {orderItems.length})
        </h2>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price/Unit
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {orderItems.map((item) => {
                // ✅ CORRECT IMAGE URL EXTRACTION
                const productImages = item.Product?.images;
                const itemImageUrl =
                  productImages && productImages[0]?.image_url
                    ? getImageUrl(productImages[0].image_url)
                    : `https://placehold.co/100x100/E0BBE4/FFFFFF?text=No+Image`;

                // Determine the product link
                const productLink = item.Product?.product_id
                  ? `/product/${item.Product.product_id}` // Use the requested URL structure
                  : null;
                // ------------------------------------

                return (
                  <tr key={item.order_item_id} className="hover:bg-gray-50">
                    <td className="p-2">
                      <a href={productLink}>
                        <img
                          src={itemImageUrl}
                          alt={item.Product?.name_en || "Product"}
                          className="w-16 h-16 object-cover rounded-md"
                        />
                      </a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {/* Assuming item.Product.name_en is available from EAGER-LOADING */}
                      <a
                        href={productLink}
                        className=" hover:text-grey-800 hover:underline font-semibold"
                      >
                        {item.Product
                          ? item.Product.name_en
                          : t("profile.orderDetails.productNameMissing")}
                      </a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                      {item.quantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                      {formatCurrency(item.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                      {formatCurrency(item.quantity * item.price)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* --- TOTALS SUMMARY --- */}
        <div className="flex justify-end mt-6">
          <div className="w-full sm:w-80 space-y-2 p-4 border rounded-lg bg-gray-50">
            <div className="flex justify-between font-medium text-gray-700">
              <span>{t("profile.orderDetails.subtotalLabel")}</span>
              <span>{formatCurrency(itemsTotal)}</span>
            </div>
            <div className="flex justify-between font-medium text-gray-700">
              <span>{t("profile.orderDetails.shippingLabel")}</span>
              <span>{formatCurrency(order.shipping_cost)}</span>
            </div>
            <div className="flex justify-between font-extrabold text-lg pt-2 border-t border-gray-300 text-[#83375B]">
              <span>{t("profile.orderDetails.orderTotalLabel")}</span>
              <span>{formatCurrency(order.total_price)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsPage;
